// The candidate's outcome in the room (D14, CR-029): his six words as one RadioTile rail in his order, a rationale and
// the gated Record outcome. The gate's rows are D12's: Missing attendee · Missing scorecard · Alignment · HM/Recruiter
// agreement (an exception round only), then the rationale. Reopen records a search outcome (source room); the others
// record the decision the word maps to. The Recruiter records in the room with source in_room, provisional until the
// Hiring Manager confirms.
// B14 (C4, DC-249): Primary (extend an offer) needs every participant aligned on Hire, and Strong Hire counts as Hire —
// the room's `level.hire_like`; the Alignment row counts, for Primary, everyone not aligned on a hire-like value. The
// server's refusal (`alignment_on_hire`) names them.
import { useState } from "react";
import {
  ACTION_LABELS, Card, COMMON_LABELS, DEBRIEF_OUTCOME_LABELS, DEBRIEF_READINESS_STATUS, DEBRIEF_ROOM_LABELS, DEBRIEF_STATUS, DIFF_LABELS, Gate, RadioTile,
  RULE_LABELS, Stack, TextArea,
} from "@/mc";
import type { GateRow, RadioTileOption } from "@/mc";
import { debriefRecordOutcome, debriefRecordSearchOutcome, type DebriefOpenOutput } from "@/api/tools";
import { errorText, useWrite } from "@/api/useTool";
import { levelOf } from "@/views/b7/room/reco";
import type { RoomPanelProps } from "@/views/b7/room/types";
import {
  attendees, DECIDE_LABELS, DECIDE_RULES, DECISION_OF, HIS_OUTCOMES, memberOf, reasonOk, roomOf, ROOM_KEYS,
  type HisOutcome,
} from "./labels";

const WORD: Readonly<Record<HisOutcome, string>> = {
  primary: DEBRIEF_OUTCOME_LABELS.primary,
  alternates: DEBRIEF_OUTCOME_LABELS.alternates,
  continue: DEBRIEF_OUTCOME_LABELS.continue,
  reopen: DEBRIEF_OUTCOME_LABELS.reopen,
  exception_round: DEBRIEF_OUTCOME_LABELS.additionalInterviewException,
  others: DEBRIEF_OUTCOME_LABELS.dispositionOthers,
};

/** The server flag that admits each word, when it sends one (V1's names; a missing flag leaves it to the server). */
const FLAG: Readonly<Partial<Record<HisOutcome, string>>> = {
  primary: "record_outcome_extend",
  alternates: "record_outcome_alternate",
  others: "record_outcome_negative",
};

/** The word a recorded decision reads back as. */
const wordOf = (value: unknown): HisOutcome | "" =>
  (HIS_OUTCOMES.find((w) => DECISION_OF[w] !== null && DECISION_OF[w] === value) ?? "");

/** An agreed exception request stands for this candidate (D15: requester plus the other party). */
const agreedFor = (view: DebriefOpenOutput, applicationId: string) =>
  (roomOf(view)?.exception_requests ?? []).some(
    (q) => q.status === "agreed" && q.application_id === applicationId,
  );

/** D12's gate over one candidate: the four rows his rule names, plus the rationale the record carries. */
export function outcomeGate(view: DebriefOpenOutput, applicationId: string, choice: HisOutcome | "", rationale: string): GateRow[] {
  const member = memberOf(view, applicationId);
  const reasons = view.blocked_reasons ?? [];
  const missingPeople = attendees(view).filter((a) => a.status === null || a.status === "absent").length;
  const missingAttendee = Math.max(missingPeople, reasons.some((r) => /attendee/.test(r)) ? 1 : 0);
  const missingCards = Math.max(member?.scorecards_due ?? 0, reasons.some((r) => /scorecard/.test(r)) ? 1 : 0);
  const rows = member?.alignments ?? [];
  const { hireLike } = levelOf(view);
  const unaligned = choice === "primary"
    ? rows.filter((a) => typeof a.value !== "string" || !hireLike.includes(a.value)).length
    : rows.filter((a) => a.value === null || a.value === undefined).length;
  const agreed = agreedFor(view, applicationId);
  const needsAgreement = choice === "exception_round";
  return [
    { status: missingAttendee ? "bad" : "ok", text: DEBRIEF_ROOM_LABELS.missingAttendee, count: missingAttendee || undefined },
    { status: missingCards ? "bad" : "ok", text: DECIDE_LABELS.missingScorecard, count: missingCards || undefined },
    { status: unaligned ? "todo" : "ok", text: DEBRIEF_STATUS.alignment, count: unaligned || undefined },
    { status: needsAgreement && !agreed ? "todo" : "ok", text: DECIDE_LABELS.hmRecruiterAgreement },
    { status: choice && reasonOk(rationale) ? "ok" : "todo", text: DIFF_LABELS.rationale },
  ];
}

export function OutcomeCard({ view, applicationId, onChanged }: RoomPanelProps & { scorecardId?: string | undefined }) {
  const decide = useWrite(debriefRecordOutcome, ROOM_KEYS);
  const reopen = useWrite(debriefRecordSearchOutcome, ROOM_KEYS);
  const member = memberOf(view, applicationId);
  const [choice, setChoice] = useState<HisOutcome | "">("");
  const [rationale, setRationale] = useState("");
  const [refused, setRefused] = useState<string | null>(null);
  if (!member) return null;
  const recorded = member.outcome ? wordOf(member.outcome.value) : "";
  const decided = member.outcome !== null || member.withdrawn;
  const mayRecord = member.can.record_outcome !== false || member.can.record_outcome_in_room === true;
  const edit = !decided && mayRecord;
  const agreed = agreedFor(view, applicationId);
  const options: RadioTileOption[] = HIS_OUTCOMES.map((word, at) => {
    const flag = FLAG[word];
    const shut = flag !== undefined && member.can[flag] === false;
    const blocked = word === "exception_round" && !agreed
      ? { reason: DECIDE_RULES.exceptionRule }
      : word === "reopen" && view.can.record_search_outcome === false
        ? { reason: RULE_LABELS.noFinalizationMissing }
        : shut ? { reason: RULE_LABELS.noFinalizationMissing } : undefined;
    return { value: word, label: WORD[word], blocked, hs: at === 0 ? "room.outcome" : undefined };
  });
  const rows = outcomeGate(view, applicationId, choice, rationale);
  const ready = rows.every((r) => r.status === "ok");
  const record = async () => {
    if (!choice || !ready) return;
    setRefused(null);
    try {
      const decision = DECISION_OF[choice];
      if (decision === null) {
        await reopen.mutateAsync({ requisition_id: view.requisition_id, cycle_id: view.cycle_id, rationale: rationale.trim() });
      } else {
        await decide.mutateAsync({
          requisition_id: view.requisition_id,
          cycle_id: view.cycle_id,
          application_id: applicationId,
          outcome: decision,
          rationale: rationale.trim(),
          // D14: the Recruiter records for the Hiring Manager in the room, provisional until the HM confirms.
          ...(member.can.record_outcome_in_room === true ? { source: "in_room" as const } : {}),
        });
      }
      setChoice("");
      setRationale("");
      onChanged();
    } catch (e) {
      setRefused(errorText(e));
    }
  };
  return (
    <Card title={edit ? ACTION_LABELS.recordOutcome : DEBRIEF_STATUS.outcomeRecorded} refused={refused ? { reason: refused } : undefined}>
      <Stack gap={12}>
        <RadioTile
          label={edit ? ACTION_LABELS.recordOutcome : DEBRIEF_STATUS.outcomeRecorded}
          options={options}
          value={edit ? choice : recorded}
          onChange={(v) => setChoice(v as HisOutcome)}
          mode={edit ? "edit" : "view"}
        />
        {edit ? (
          <TextArea label={DIFF_LABELS.rationale} required value={rationale} onChange={(e) => setRationale(e.target.value)} />
        ) : null}
        {edit ? (
          <Gate
            below
            verdict={ready ? DEBRIEF_READINESS_STATUS.ready : DEBRIEF_READINESS_STATUS.notReady}
            rows={rows}
            ready={ready}
            action={{ label: ACTION_LABELS.recordOutcome, onClick: () => void record(), busy: decide.isPending || reopen.isPending, hs: "room.record-outcome" }}
          />
        ) : null}
      </Stack>
    </Card>
  );
}
